/* pantallas-alumno (F): EL RENGLON DE SESION DEL HISTORIAL (parte 2, "Piezas nuevas" y seccion 1).

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Tarjeta de 76 minimo, #141414, borde #222222, radio 16, relleno 14 12 14 16. Icono del modo de 40 en
   el cuadro naranja suave del Panel (los tres modos con el mismo naranja). Renglon 1: el modo y su chip.
   Renglon 2: los nombres, cada uno con su icono, en un renglon con "..." y separados por el punto
   DIBUJADO; tocarlo abre el panel flotante del Panel con la lista completa (P3). Renglon 3: la fecha y
   "{n}/{m} correct" con el punto dibujado. A la derecha el puntaje y el bote (40; 44 en el celular). En
   el celular el puntaje sube al renglon 1, a la derecha: es UN solo nodo acomodado con la rejilla.

   LOS NOMBRES Y SU ICONO (4.5 del encargo, medido): en la web los nombres salen de `GET /study/history`
   (`routes/study.py`), que junta los nombres de las cards de una sesion en UN elemento separado por ", "
   y lo pone AL FINAL, pero la entrada no dice si la sesion tiene cards; en la app salen del motor local,
   que no junta cards. Con lo que llega no se puede saber el tipo de cada nombre, asi que todos llevan la
   hoja y ningun elemento se parte por coma (un documento puede llamarse "Calculus II, integration
   techniques.pdf"). "Deleted" (el elemento entero) va en cursiva gris, con el texto de
   `nombresDeLaSesion`.

   Mouse encima: borde #3A2414 y el bote en rojo. Foco: anillo blanco de 2 en el bote. */
import React, { useLayoutEffect, useRef, useState } from 'react';
import { BookOpen, Zap, Target, Trash2, FileText } from 'lucide-react';
import Flotante from '../panel/Flotante';
import { CAJA_ICONO, AreaDe44 } from '../panel/anatomia';
import ChipDeEstado from '../panel/ChipDeEstado';
import { ChipVerde, Puntaje, PuntoSeparador, GiroEncima, BloqueEsqueleto as Bloque } from '../alumno';
import { OPACIDAD_MENU_APAGADO } from '../alumno/estilos';
import { NOMBRE_DE_LO_BORRADO, nombresDeLaSesion } from '../../lib/textosDelMotor';

const ICONO_DEL_MODO = { exam: BookOpen, flashcards: Zap, adaptive: Target };

/** Los cortes de hoy del puntaje del Historial: 70 o mas alto, 40 a 69 medio, abajo bajo. */
export function nivelDelPuntaje(score) {
  if (score >= 70) return 'alto';
  if (score >= 40) return 'medio';
  return 'bajo';
}

/** Los nombres de la sesion como piezas {texto, borrado}: un elemento del servidor, una pieza. */
export function piezasDeNombres(t, nombres) {
  if (!Array.isArray(nombres)) return [];
  const traducidos = nombresDeLaSesion(t, nombres);
  return nombres.map((crudo, i) => ({ texto: traducidos[i], borrado: crudo === NOMBRE_DE_LO_BORRADO }));
}

function Nombre({ pieza, enLista = false }) {
  return (
    <span data-icono="hoja" className={enLista ? 'flex items-start gap-2 min-w-0' : 'inline'}>
      <FileText aria-hidden="true"
        className={enLista ? 'w-3.5 h-3.5 mt-[3px] shrink-0 text-[#9A9A9A]' : 'inline-block w-3.5 h-3.5 mr-1 align-[-2px] text-[#7E7E7E]'} />
      <span className={`${pieza.borrado ? 'italic text-[#7E7E7E]' : ''}${enLista ? ' min-w-0 break-words' : ''}`.trim() || undefined}>{pieza.texto}</span>
    </span>
  );
}

/* Medicion (HI2-b y HI14-b): el flotante de los nombres sale debajo de la tarjeta (pieza del Panel) y quedaba encima de
   la tarjeta siguiente con su ultimo renglon asomando por debajo. Esta pieza vive DENTRO del flotante: mide su caja al
   abrirse (y al cambiar el ancho de la ventana) y le pide a la tarjeta que reserve abajo ese alto mas el hueco de 8 que
   los separa; al cerrarse, la reserva vuelve a cero. */
const HUECO_DEL_FLOTANTE = 8;
function ReservaDelFlotante({ testid, alMedir }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const medir = () => {
      const caja = ref.current ? ref.current.closest(`[data-testid="${testid}"]`) : null;
      alMedir(caja ? caja.offsetHeight + HUECO_DEL_FLOTANTE : 0);
    };
    medir();
    window.addEventListener('resize', medir);
    return () => { window.removeEventListener('resize', medir); alMedir(0); };
  }, [testid, alMedir]);
  return <span ref={ref} hidden />;
}

function EstadoDeLaSesion({ s, t }) {
  if (s.status === 'abandoned') return <ChipDeEstado tono="naranja">{t('study:log.status_badges.abandoned')}</ChipDeEstado>;
  if (s.completed) return <ChipVerde>{t('study:log.status_badges.completed')}</ChipVerde>;
  return <ChipDeEstado tono="neutro">{t('study:log.status_badges.in_progress')}</ChipDeEstado>;
}

export default function RenglonDeSesion({
  s, t, nombreDelModo, fecha, borrando = false, sinRed = false, alBorrar, alTocarSinRed,
}) {
  const IconoModo = ICONO_DEL_MODO[s.mode] || BookOpen;
  const conPuntaje = s.score !== null && s.score !== undefined && s.completed;
  const piezas = piezasDeNombres(t, s.document_names);
  const razon = s.mode === 'exam' && s.total_questions > 0
    ? t('study:log.correct_ratio', { correct: s.correct_count, total: s.total_questions })
    : s.mode === 'adaptive' && s.total_answered > 0
      ? t('study:log.correct_ratio', { correct: s.correct_count, total: s.total_answered })
      : null;
  const etiquetaBote = t('study:log.delete_btn_aria');
  const [reserva, setReserva] = useState(0);
  const alTocarBote = (e) => {
    e.stopPropagation();
    if (sinRed) { if (alTocarSinRed) alTocarSinRed(); return; }
    if (borrando) return;
    if (alBorrar) alBorrar(s.id);
  };

  return (
    <div data-testid={`session-${s.id}`} style={{ marginBottom: reserva }}
      className="relative group grid grid-cols-[52px_minmax(0,1fr)_auto_auto] items-center min-h-[76px] rounded-[16px] border border-[#222222] bg-[#141414] pt-3.5 pb-3.5 pr-3 pl-4 transition-colors hoverable:border-[#3A2414]">
      <div className={`${CAJA_ICONO} col-start-1 row-start-1 row-span-3 self-center`} aria-hidden="true">
        <IconoModo className="w-5 h-5 text-[#F7A06A]" />
      </div>

      <div className="col-start-2 row-start-1 flex items-center gap-2 min-w-0">
        <span className="text-[15px] leading-5 font-semibold text-[#F4F4F3] truncate min-w-0">{nombreDelModo}</span>
        <span className="shrink-0"><EstadoDeLaSesion s={s} t={t} /></span>
      </div>

      {conPuntaje ? (
        <div className="col-start-3 row-start-1 pl-3 md:row-span-3 md:self-center">
          <Puntaje nivel={nivelDelPuntaje(s.score)} testid={`session-score-${s.id}`}>{s.score}%</Puntaje>
        </div>
      ) : null}

      {piezas.length > 0 ? (
        <div className="col-start-2 row-start-2 col-span-2 md:col-span-1 min-w-0 mt-1">
          {/* Medicion (HI1, HI14): el boton y la linea de nombres son RELATIVOS. El ", " escondido para el lector es
              absoluto: con el boton sin posicion, su caja era la tarjeta, el "..." no lo recortaba y quedaba a la
              derecha de todo, deslizando la pagina. El area de 44 va directa en el boton, fuera de lo que recorta. */}
          <Flotante
            testid={`session-names-list-${s.id}`}
            className="max-h-[60vh] overflow-y-auto overscroll-contain"
            boton={(props) => (
              <button type="button" {...props} data-testid={`session-names-${s.id}`}
                title={piezas.map((p) => p.texto).join(', ')}
                className="relative block w-full min-w-0 text-left text-[13px] leading-[18px] text-[#9A9A9A] rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                <AreaDe44 />
                <span data-parte="nombres" className="relative block truncate">
                  {piezas.map((p, i) => (
                    <React.Fragment key={i}>
                      {i > 0 ? <PuntoSeparador /> : null}
                      {i > 0 ? <span className="sr-only">, </span> : null}
                      <Nombre pieza={p} />
                    </React.Fragment>
                  ))}
                </span>
              </button>
            )}
            contenido={() => (
              <>
                <ReservaDelFlotante testid={`session-names-list-${s.id}`} alMedir={setReserva} />
                <ul className="flex flex-col gap-2 text-[13px] leading-[18px] text-[#C8C8C8]">
                  {piezas.map((p, i) => (
                    <li key={i} data-testid={`session-name-row-${s.id}-${i}`}><Nombre pieza={p} enLista /></li>
                  ))}
                </ul>
              </>
            )}
          />
        </div>
      ) : null}

      <div data-testid={`session-meta-${s.id}`}
        className="col-start-2 row-start-3 col-span-2 md:col-span-1 min-w-0 mt-1 flex items-center text-[12.5px] leading-[18px] text-[#9A9A9A] whitespace-nowrap overflow-hidden">
        <span className="shrink-0">{fecha}</span>
        {razon ? (
          <>
            <PuntoSeparador />
            <span className="sr-only">, </span>
            <span className="truncate min-w-0">{razon}</span>
          </>
        ) : null}
      </div>

      <div className="col-start-4 row-start-1 row-span-3 self-start md:self-center pl-2">
        <button type="button" onClick={alTocarBote} title={etiquetaBote} aria-label={etiquetaBote}
          aria-disabled={sinRed ? 'true' : undefined} disabled={!sinRed && borrando}
          style={sinRed ? { opacity: OPACIDAD_MENU_APAGADO } : undefined}
          data-testid={`delete-session-${s.id}`}
          className="relative w-11 h-11 md:w-10 md:h-10 rounded-xl flex items-center justify-center text-[#7E7E7E] transition-colors group-hover:text-[#F87171] group-hover:bg-[#2A1414] focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed">
          <GiroEncima trabajando={borrando}><Trash2 className="w-[18px] h-[18px]" aria-hidden="true" /></GiroEncima>
        </button>
      </div>
    </div>
  );
}

/* El esqueleto del Historial (HI6): el renglon del conteo y cinco tarjetas con el cuadro del icono, tres
   barras y la del puntaje. Mismas medidas que el renglon de verdad: nada brinca al llegar los datos. */
export function EsqueletoDelHistorial({ filas = 5, testid = 'skeleton-study-log' }) {
  return (
    <div data-testid={testid} className="flex flex-col gap-[10px]" aria-hidden="true">
      <Bloque className="h-4 w-64 max-w-full mb-1" />
      {Array.from({ length: filas }, (_, i) => (
        <div key={i} className="grid grid-cols-[52px_minmax(0,1fr)_auto] items-center min-h-[76px] rounded-[16px] border border-[#222222] bg-[#141414] pt-3.5 pb-3.5 pr-3 pl-4">
          <Bloque className="w-10 h-10 rounded-xl" />
          <div className="flex flex-col gap-2 min-w-0">
            <Bloque className="h-4 w-40 max-w-full" />
            <Bloque className="h-3 w-3/5" />
            <Bloque className="h-3 w-2/5" />
          </div>
          <Bloque className="h-4 w-12 ml-3" />
        </div>
      ))}
    </div>
  );
}
